import { debounce } from "lodash";
import { SearchIcon } from "lucide-react-native";
import { ComponentProps, ComponentRef, forwardRef, useMemo } from "react";
import { Input, InputField, InputIcon, InputSlot } from "../../ui/input";
import { Spinner } from "../../ui/spinner";

type SearchProps = {
  size?: ComponentProps<typeof Input>["size"];
  keyboardType?: ComponentProps<typeof InputField>["keyboardType"];
  textContentType?: ComponentProps<typeof InputField>["textContentType"];
  autoCapitalize?: ComponentProps<typeof InputField>["autoCapitalize"];
  placeholder?: string;
  onChange: (text: string) => void;
  loading?: boolean;
  debounceTime?: number;
  inputFieldProps?: ComponentProps<typeof InputField>;
  inputProps?: ComponentProps<typeof Input>;
  /** Extra classes on the Input wrapper (e.g. "w-full" to fill the parent). */
  className?: string;
};

const Search = forwardRef<ComponentRef<typeof InputField>, SearchProps>(
  (
    {
      size,
      placeholder,
      onChange,
      keyboardType,
      textContentType,
      autoCapitalize,
      loading,
      debounceTime = 500,
      inputFieldProps,
      inputProps,
      className,
    },
    ref,
  ) => {
    const debouncedOnChange = useMemo(
      () => debounce(onChange, debounceTime),
      [onChange, debounceTime],
    );
    return (
      <Input size={size} className={className} {...inputProps}>
        <InputField
          ref={ref}
          placeholder={placeholder}
          onChangeText={debouncedOnChange}
          keyboardType={keyboardType}
          textContentType={textContentType}
          autoCapitalize={autoCapitalize}
          {...inputFieldProps}
        />
        <InputSlot className="pr-3">
          {loading ? <Spinner size="small" /> : <InputIcon as={SearchIcon} />}
        </InputSlot>
      </Input>
    );
  },
);

Search.displayName = "Search";

export default Search;
